import { useEffect, useState } from 'react';
import { getListPrice } from 'src/common/hooks/widgets/fetchData';
import { Product } from '../constants/defaults';
import { Tiers, tierData } from '../constants/prices';
import { LangDefaults } from 'src/common/hooks/widgets/useLang';

export interface priceProps {
  partNumber: string;
  pricingIndicator: string;
  pricingModel: number;
  scaleQtyPrice: [
    {
      qty: number;
      price: number;
    },
  ];
}

const partNumber = {
  SaaS: {
    platform: 'D03YUZX',
    env: 'D03YQZX',
    ur: 'D03YNZX',
    ar: 'D03YPZX',
  },
  Software: {
    platform: 'D299TLL',
    env: 'D299ULL',
    ur: 'D299VLL',
    ar: 'D299WLL',
  },
};

type typeOfPart = {
  qty: number;
  price: number;
};

export const partsToQuery = [
  {
    partNumber: 'D299TLL',
  },
  {
    partNumber: 'D299ULL',
  },
  {
    partNumber: 'D299VLL',
  },
  {
    partNumber: 'D299WLL',
  },
  {
    partNumber: 'D03YUZX',
  },
  {
    partNumber: 'D03YQZX',
  },
  {
    partNumber: 'D03YNZX',
  },
  {
    partNumber: 'D03YPZX',
  },
];

export const setPrice = (
  numberOfUnattendedRobots: number,
  numberOfEnvironments: number,
  numberOfAttendedRobots: number,
  productToAcquire: Product.SaaS | Product.Software,
  expandingConfiguration: string,
  setShowContactUs: React.Dispatch<React.SetStateAction<boolean>>,
  setYearPrice: React.Dispatch<React.SetStateAction<number>>,
  currency: string,
  country: string,
  userLocale: string,
  setCost: React.Dispatch<React.SetStateAction<number>>,
) => {
  // Check language and prices
  const [priceFile, setPriceFile] = useState({} as priceProps[]);
  const [usPriceFile, setUSPriceFile] = useState({} as priceProps[]);
  /**
   *
   */
  useEffect(() => {
    // We need now to determine always the price as USA and in USD currency
    getODSListPrice(LangDefaults.localCurrency, LangDefaults.country).then(
      (data) => {
        setUSPriceFile(data);
        // Determine the list price for the country and currency rquested by the portal
        getODSListPrice(currency, country).then((data) => {
          setPriceFile(data);
        });
      },
    );
  }, [userLocale, currency, country]);

  //
  const getODSListPrice = async (
    currency: string,
    country: string,
  ): Promise<priceProps[]> => {
    let listPrice = {} as priceProps[];
    const body: any = {
      currency: currency,
      countryCode3: country,
      parts: partsToQuery,
    };
    if (currency.length === 0 || country.length === 0) {
      return listPrice;
    }
    listPrice = await getListPrice(
      `https://api.marketplace.ibm.com/purchase/catalog/resources/price/info`,
      body,
      'POST',
    ).then((data) => {
      if (data.parts && data.parts.length !== 0) {
        return data.parts;
      } else {
        return listPrice;
      }
    });
    return listPrice;
  };

  /**
   *
   */
  useEffect(() => {
    let numberLeftOfVms = numberOfEnvironments;
    let numberleftOfUR = numberOfUnattendedRobots;
    let numberLeftOfAR = numberOfAttendedRobots;
    let price = 0;
    let usPrice = 0;
    if (expandingConfiguration === 'No') {
      if (
        numberOfEnvironments < 1 ||
        numberOfUnattendedRobots < 1 ||
        numberOfAttendedRobots < 2
      ) {
        setShowContactUs(true);
        setYearPrice(price);
        setCost(price);
        return;
      }
      numberLeftOfVms = numberOfEnvironments - 1;
      numberleftOfUR = numberOfUnattendedRobots - 1;
      numberLeftOfAR = numberOfAttendedRobots - 2;
      if (
        numberOfEnvironments >= 1 &&
        numberOfAttendedRobots >= 2 &&
        numberOfUnattendedRobots >= 1
      ) {
        price += findPart(1, partNumber[productToAcquire].platform, priceFile);
        usPrice += findPart(
          1,
          partNumber[productToAcquire].platform,
          usPriceFile,
        );
      }
    }
    if (priceFile.length === 0) {
      setShowContactUs(true);
      return;
    }
    setShowContactUs(false);
    price +=
      findPart(numberLeftOfVms, partNumber[productToAcquire].env, priceFile) +
      findPart(numberleftOfUR, partNumber[productToAcquire].ur, priceFile) +
      findPart(numberLeftOfAR, partNumber[productToAcquire].ar, priceFile);
    usPrice +=
      findPart(numberLeftOfVms, partNumber[productToAcquire].env, usPriceFile) +
      findPart(numberleftOfUR, partNumber[productToAcquire].ur, usPriceFile) +
      findPart(numberLeftOfAR, partNumber[productToAcquire].ar, usPriceFile);
    const [tier, nextTier] = determine_tier(usPrice);
    price = determine_price(price, tier, nextTier, productToAcquire);
    usPrice = determine_price(usPrice, tier, nextTier, productToAcquire);
    setYearPrice(price);
    setCost(usPrice);
  }, [
    numberOfUnattendedRobots,
    numberOfEnvironments,
    numberOfAttendedRobots,
    productToAcquire,
    expandingConfiguration,
    priceFile,
  ]);

  /**
   * Find parts in the list price array
   */
  const findPart = (
    quantity: number,
    part: string,
    priceFile: priceProps[],
  ): number => {
    let value = {} as typeOfPart | undefined;
    // Find product
    let tierPrice = 0;
    if (priceFile?.length) {
      const turboPrice = priceFile.find((i) => i.partNumber == part);
      if (turboPrice) {
        value = turboPrice.scaleQtyPrice.find((i) => i.qty == 1);
        if (value) {
          tierPrice = Math.round(value?.price * quantity);
        }
      }
    }
    return tierPrice;
  };

  /**
   *
   */
  const determine_tier = (value: number) => {
    let tier = {} as tierData;
    let nextTtier = {} as tierData;
    if (value >= Tiers[0].min && value <= Tiers[0].max) {
      tier = Tiers[0];
    }
    if (value >= Tiers[1].min && value <= Tiers[1].max) {
      tier = Tiers[1];
      nextTtier = Tiers[0];
    }
    if (value >= Tiers[2].min && value <= Tiers[2].max) {
      tier = Tiers[2];
      nextTtier = Tiers[1];
    }
    if (value >= Tiers[3].min && value <= Tiers[3].max) {
      tier = Tiers[3];
      nextTtier = Tiers[2];
    }
    return [tier, nextTtier];
  };

  /**
   *
   */
  const determine_price = (
    listPrice: number,
    tier: tierData,
    nextTier: tierData,
    productToAcquire: Product,
  ) => {
    let price = 0;
    const amountLeft = listPrice - tier.min;
    const differenceMaxMin = tier.max - tier.min;
    const proportionInTier = amountLeft / differenceMaxMin;
    // We force to check T1 as we need price at that level to be with no discount
    if (Object.keys(nextTier).length === 0 || tier.tier == 'T1') {
      price = listPrice * (1 - tier[productToAcquire]);
    } else {
      const discountToApply =
        tier[productToAcquire] +
        proportionInTier *
          (nextTier[productToAcquire] - tier[productToAcquire]);
      price = listPrice * (1 - discountToApply);
    }
    return price;
  };
};
